import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Helmet } from 'react-helmet-async'

import {
  PageWrapper,
  Loader,
  ImpactsCardsVer,
  PreviousPage,
  NextPage,
  PageFooter,
  FullWidthSection,
} from 'src/components'
import { useAccount, useImpacts, useNavigationAvailability } from 'src/hooks'
import { LeftDivider, TopDivider } from '../product/product.styled'
import { LayoutContainer } from 'src/components/layout-container'
import { DEFAULT_LANGUAGE, PATHS, ROUTES } from 'src/constants'
import { useEffect } from 'react'
import { Typography, useTheme } from '@mui/material'
import { FlaskIcon } from 'src/assets/icons/v2/FlaskIcon'
import { JOURNEY_PAGE_TEMPLATE_ID } from 'src/templates/constants'
import { FairtradeImpactsTemplate } from 'src/templates/fairtrade/impacts'
import { buildProductNavPaths, ensureProductHomePath } from 'src/utils'

export default function ImpactsPage() {
  const [t, i18n] = useTranslation()
  const { simpleProductId } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const theme = useTheme()

  const { impacts, isLoading: isImpactsLoading, product } = useImpacts()
  const { data: account, isLoading: isAccountLoading } = useAccount(
    product?.account_id,
    i18n.language,
  )
  useEffect(() => {
    if (!isImpactsLoading && product?.name) {
      document.title = `Trackgood Journey ${product?.name} Impacts`
    }
  }, [isImpactsLoading, product?.name])

  useEffect(() => {
    const impactId = location.hash?.substring(1) // Get the impact ID from the URL hash

    if (!isImpactsLoading && impacts?.length > 0) {
      if (impactId) {
        // Delay the scroll to ensure elements are rendered
        setTimeout(() => {
          const impactElement = document.getElementById(impactId)
          if (impactElement) {
            const elementPosition = impactElement.getBoundingClientRect().top + window.scrollY
            const offsetPosition = elementPosition - 35

            // Scroll directly to the calculated position
            window.scrollTo({
              top: offsetPosition,
              behavior: 'smooth',
            })
          } else {
            window.scrollTo({ top: 0, behavior: 'smooth' })
          }
        }, 10)
      }
    }
  }, [isImpactsLoading, impacts, location.hash])

  useEffect(() => {
    if (impacts && impacts.length === 0 && !isImpactsLoading) {
      if (location.state?.to) {
        navigate(
          i18n.language === DEFAULT_LANGUAGE
            ? `/${ROUTES.ACCOUNT}/${product?.account_simple_id}`
            : `/${i18n.language}/${ROUTES.ACCOUNT}/${product?.account_simple_id}`,
          { state: { from: location.pathname, productId: simpleProductId } },
        )
        return
      }
      navigate(location.pathname.replace(`/${PATHS.IMPACTS}`, ''))
    }
  }, [impacts])

  const { hasJourney: hasJourneyPage } = useNavigationAvailability({
    product,
    productId: product?.id,
    simpleProductId,
  })

  const { journeyPath: journeyTo, impactsPath: impactsTo } = buildProductNavPaths(location.pathname)
  const previousFrom = ensureProductHomePath(location.state?.from)

  if (!product || isImpactsLoading || isAccountLoading || !account) {
    return <Loader />
  }

  // --- SEO (Helmet) ---
  const holder = account?.account_holder ?? 'the brand'
  const productName = product?.name ?? t('impacts-default-product-name')
  const impactsTitle = `Trackgood Journey ${productName} Impacts`
  const impactsDesc = `See the verified impacts of ${productName} from ${holder} on Trackgood Journey.`
  const canonical = `${window.location.origin}${window.location.pathname}`

  const isFairtradeTemplate =
    account?.journey_page_template_id === JOURNEY_PAGE_TEMPLATE_ID.FAIRTRADE

  const productLabel = t('journey-navigation-product-button')
  const journeyLabel = t('journey-navigation-journey-button')
  const accountLabel = t('journey-navigation-account-button')
  const impactsLabel = t('journey-navigation-impacts-button')

  const fairtradeNavColor = account?.alternative_color || '#DBFF4C'

  const previousLabel =
    location.state?.previousLabel || (hasJourneyPage ? journeyLabel : productLabel)
  const nextLabel = account?.account_holder
    ? `${accountLabel} ${account.account_holder}`
    : 'About this account'

  return (
    <LayoutContainer>
      <Helmet>
        <title id="index__pages-impacts__title__l94">{impactsTitle}</title>
        <meta id="index__pages-impacts__meta__l95" name="description" content={impactsDesc} />
        <link id="index__pages-impacts__link__l96" rel="canonical" href={canonical} />

        <meta id="index__pages-impacts__meta__l98" property="og:title" content={impactsTitle} />
        <meta
          id="index__pages-impacts__meta__l99"
          property="og:description"
          content={impactsDesc}
        />

        <meta id="index__pages-impacts__meta__l100" property="og:url" content={canonical} />

        <meta id="index__pages-impacts__meta__l102" name="twitter:title" content={impactsTitle} />
        <meta
          id="index__pages-impacts__meta__l103"
          name="twitter:description"
          content={impactsDesc}
        />
      </Helmet>

      {!isFairtradeTemplate && (account?.alternative_color || product?.ai_generated) && (
        <TopDivider id="index__pages-impacts__topdivider__l107" bgColor={account.alternative_color}>
          {product?.ai_generated && (
            <>
              <Typography
                id="index__pages-impacts__typography__l110"
                sx={{
                  fontSize: '10px',
                  fontWeight: 700,
                  color: theme.palette.common.white,
                  mt: '2px',
                }}
                textAlign={'center'}
              >
                {t('ai-generated-label')}
              </Typography>
              <FlaskIcon size={14} />
            </>
          )}
        </TopDivider>
      )}
      {isFairtradeTemplate && (
        <LeftDivider
          id="index__pages-impacts__leftdivider__l160"
          bgColor={account?.alternative_color || account?.primary_color}
        />
      )}

      <PageWrapper>
        <PreviousPage
          to={previousFrom ?? journeyTo}
          absolute
          // topOffset={isFairtradeTemplate ? 58 : 0}
          variant={isFairtradeTemplate ? JOURNEY_PAGE_TEMPLATE_ID.FAIRTRADE : 'default'}
          label={isFairtradeTemplate ? previousLabel : undefined}
          backgroundColor={isFairtradeTemplate ? fairtradeNavColor : undefined}
          textColor={isFairtradeTemplate ? '#111111' : undefined}
        />

        <NextPage
          to={
            i18n.language === DEFAULT_LANGUAGE
              ? `/${ROUTES.ACCOUNT}/${product?.account_simple_id}`
              : `/${i18n.language}/${ROUTES.ACCOUNT}/${product?.account_simple_id}`
          }
          state={{
            from: impactsTo,
            productId: simpleProductId,
            previousLabel: impactsLabel,
          }}
          // topOffset={isFairtradeTemplate ? 58 : 0}
          variant={isFairtradeTemplate ? JOURNEY_PAGE_TEMPLATE_ID.FAIRTRADE : 'default'}
          label={isFairtradeTemplate ? nextLabel : undefined}
          backgroundColor={isFairtradeTemplate ? fairtradeNavColor : undefined}
          textColor={isFairtradeTemplate ? '#111111' : undefined}
        />

        {isFairtradeTemplate ? (
          <FairtradeImpactsTemplate
            items={impacts}
            showUnSdg={account?.show_unsgd_impacts_page}
            primaryColor={account.primary_color}
            walletAddress={account.wallet_address}
            showWalletAddress={account.show_wallet_address}
            showBcInfo={account.show_bc_info}
            showLatestBcOnImpacts={account.show_latest_bc_on_impacts}
            accountImage={account?.image_small || account?.image_medium}
            productName={product?.name}
          />
        ) : (
          <FullWidthSection
            id="index__pages-impacts__fullwidthsectionst__l110"
            idInner="index__pages-impacts__fullwidthsectioninner__l110"
            sxInner={{ p: '20px' }}
          >
            <ImpactsCardsVer
              items={impacts}
              showUnSdg={account?.show_unsgd_impacts_page}
              primaryColor={account.primary_color}
              walletAddress={account.wallet_address}
              showWalletAddress={account.show_wallet_address}
              showBcInfo={account.show_bc_info}
              showLatestBcOnImpacts={account.show_latest_bc_on_impacts}
              accountImage={account?.image_small || account?.image_medium}
            />
          </FullWidthSection>
        )}
      </PageWrapper>
      <PageFooter />
    </LayoutContainer>
  )
}
